import { z } from "zod";

import type { ModuleDataMap } from "../response/types";
import type {
  ContentInstancePosition,
  ModuleID,
  ModuleInstanceKey,
  ModuleInstancePosition,
} from "../types/ids";

const LAYOUT_POSITION = "layout";
const SLOT_SEPARATOR = "/";
const SLOT_NAME = /^[A-Za-z0-9_-]+$/;
const CONTENT_POSITION = /^(0|[1-9][0-9]*)(\/[A-Za-z0-9_-]+)*$/;
const SLOT_POSITION_PREFIX = /^[0-9]+\//;

/** Instance key of the module ref at `position`. */
export function moduleInstanceKey(
  position: ModuleInstancePosition,
  moduleId: ModuleID,
): ModuleInstanceKey {
  return `${position}.${moduleId}`;
}

/** Instance key of a view's effective layout module. */
export function layoutInstanceKey(moduleId: ModuleID): ModuleInstanceKey {
  return moduleInstanceKey(LAYOUT_POSITION, moduleId);
}

/** Position of the ref at `index` in the view's `modules` output. */
export function contentPosition(index: number): ContentInstancePosition {
  return `${index}`;
}

/** Instance key of the module at `index` in the view's `modules` output. */
export function contentInstanceKey(
  index: number,
  moduleId: ModuleID,
): ModuleInstanceKey {
  return moduleInstanceKey(contentPosition(index), moduleId);
}

/**
 * Position of the ref in slot `slotName` of the ref at `parent`. The name is
 * not validated: the server never builds a key from an invalid one, so such a
 * key finds no data and its actions are refused rather than resolved against
 * another instance.
 */
export function slotPosition(
  parent: ContentInstancePosition,
  slotName: string,
): ContentInstancePosition {
  return `${parent}${SLOT_SEPARATOR}${slotName}`;
}

/**
 * Whether a branch slot name can appear in an instance key. `.` would end the
 * position early and `/` would read as another slot level.
 */
export function isValidSlotName(name: string): boolean {
  return SLOT_NAME.test(name);
}

/**
 * Split an instance key into its position and module id. Module ids may
 * contain `.`, so only the first `.` separates the two.
 */
export function parseModuleInstanceKey(
  key: string,
): { position: ModuleInstancePosition; moduleId: ModuleID } | undefined {
  const separator = key.indexOf(".");
  if (separator === -1) {
    return undefined;
  }
  const position = key.slice(0, separator);
  const moduleId = key.slice(separator + 1);
  if (moduleId.length === 0) {
    return undefined;
  }
  if (position === LAYOUT_POSITION) {
    return { position, moduleId };
  }
  if (CONTENT_POSITION.test(position)) {
    return { position: toContentPosition(position), moduleId };
  }
  return undefined;
}

// Rebuilt from its parts so the result carries the position's template type.
function toContentPosition(position: string): ContentInstancePosition {
  const separator = position.indexOf(SLOT_SEPARATOR);
  if (separator === -1) {
    return `${Number(position)}`;
  }
  return `${Number(position.slice(0, separator))}${SLOT_SEPARATOR}${position.slice(separator + 1)}`;
}

/**
 * Whether an instance key's position is a branch slot rather than a view-level
 * ref. Structural, so a slot key built from an invalid slot name still counts.
 */
export function isSlotInstanceKey(key: string): boolean {
  const separator = key.indexOf(".");
  return separator !== -1 && SLOT_POSITION_PREFIX.test(key.slice(0, separator));
}

/**
 * Data for one module instance. A map with no instance keys at all comes from
 * a server that predates them (FEP2-4678 rollout), so read its bare module-id
 * key — but never for a slot instance: no server emits slot data under a bare
 * id, so that entry belongs to a view-level ref. Otherwise an absent instance
 * key means the instance has no data — JSON drops `undefined` — and must not
 * borrow another instance's.
 */
export function readModuleData(
  map: ModuleDataMap,
  instanceKey: ModuleInstanceKey,
  moduleId: ModuleID,
): unknown {
  if (Object.hasOwn(map, instanceKey)) {
    return map[instanceKey];
  }
  if (isSlotInstanceKey(instanceKey)) {
    return undefined;
  }
  return Object.keys(map).some(isModuleInstanceKey) ? undefined : map[moduleId];
}

export function isModuleInstanceKey(value: unknown): value is ModuleInstanceKey {
  return (
    typeof value === "string" && parseModuleInstanceKey(value) !== undefined
  );
}

// `z.custom` rather than a refined `z.string()`: `satisfies z.ZodType<T>`
// also checks the input type, which a string schema would widen.
export const ModuleInstanceKeySchema = z.custom<ModuleInstanceKey>(
  isModuleInstanceKey,
  { message: "Invalid module instance key" },
);
